import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { quotationService } from '../services/quotationService';
import { companyService } from '../services/companyService';
import { Quotation } from '../types/quotation';
import { Company } from '../types/company';
import { Button, showToast } from '../components/UI';
import { PrinterIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

const QuotationView: React.FC = () => {
  // Add A5 print styles
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        @page {
          size: A5 portrait;
          margin: 5mm;
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [quotation, setQuotation] = useState<Quotation | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const loadData = async () => {
      try {
        setLoading(true);
        const [quotationData, companyData] = await Promise.all([
          quotationService.getQuotation(parseInt(id)),
          companyService.getActiveCompany().catch(() => null)
        ]);
        if (cancelled) return;
        setQuotation(quotationData);
        setCompany(companyData);
      } catch (error: any) {
        if (cancelled) return;
        console.error('Error loading data:', error);
        showToast.error('Failed to load quotation');
        navigate('/quotations');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();
    return () => { cancelled = true; };
  }, [id, navigate]);

  const handlePrint = () => {
    // Set A5 paper size for printing
    const printCSS = `
      @page {
        size: A5 portrait;
        margin: 5mm;
      }
      @media print {
        body {
          width: 148mm;
          height: 210mm;
        }
      }
    `;

    const styleElement = document.createElement('style');
    styleElement.innerHTML = printCSS;
    document.head.appendChild(styleElement);

    // Show reminder to user
    showToast.info('Please select A5 paper size in the print dialog');

    window.print();

    // Clean up
    setTimeout(() => {
      document.head.removeChild(styleElement);
    }, 100);
  };

  const handleBack = () => {
    navigate('/quotations');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading quotation...</p>
        </div>
      </div>
    );
  }

  if (!quotation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Quotation not found</p>
          <Button onClick={handleBack}>Go Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Print Controls - Hidden when printing */}
      <div className="print:hidden bg-white shadow-sm border-b px-6 py-4">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <Button
            onClick={handleBack}
            variant="outline"
            className="flex items-center space-x-2"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            <span>Back to Quotations</span>
          </Button>
          <Button
            onClick={handlePrint}
            className="flex items-center space-x-2"
          >
            <PrinterIcon className="w-4 h-4" />
            <span>Print Quotation</span>
          </Button>
        </div>
      </div>

      {/* Printable Content */}
      <div className="max-w-4xl mx-auto bg-white print:shadow-none print:max-w-none print:mx-0">
        <div className="p-3 sm:p-8 print:p-4">
          {/* Header */}
          <div className="mb-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-3 gap-2">
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 print:text-black mb-1">QUOTATION</h1>
                <div className="text-xs text-gray-600 print:text-black space-y-0">
                  <p><strong>Quote No:</strong> {quotation.quote_no}</p>
                  <p><strong>Version:</strong> {quotation.version}</p>
                  <p><strong>Date:</strong> {quotation.quote_date ? new Date(quotation.quote_date).toLocaleDateString('en-GB') : '-'}</p>
                </div>
              </div>
              <div className="sm:text-right min-w-0">
                <h2 className="text-base sm:text-lg font-bold text-blue-600 print:text-black mb-1 break-words">
                  {company?.name || 'Your Company Name'}
                </h2>
                <div className="text-xs text-gray-600 print:text-black space-y-0">
                  {company?.address && <p className="break-words">{company.address}</p>}
                  {(company?.city || company?.state || company?.pincode) && (
                    <p className="break-words">
                      {[company?.city, company?.state].filter(Boolean).join(', ')}
                      {company?.pincode && ` - ${company.pincode}`}
                    </p>
                  )}
                  {company?.phone && <p>Phone: {company.phone}</p>}
                  {company?.email && <p className="break-all">{company.email}</p>}
                  {company?.gst_no && <p>GST: {company.gst_no}</p>}
                  {company?.website && <p className="break-all">{company.website}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Customer Details */}
          <div className="mb-3">
            <h3 className="text-sm font-semibold mb-2 text-gray-900 print:text-black">Customer Details</h3>
            <div className="bg-gray-50 p-2 rounded">
              <div className="grid grid-cols-2 gap-2 text-xs text-gray-900 print:text-black">
                <div className="space-y-0.5 min-w-0 overflow-hidden">
                  <p className="break-words"><strong>Company:</strong> {quotation.enquiry?.company_name || '-'}</p>
                  <p className="break-words"><strong>Contact:</strong> {quotation.enquiry?.contact_person || '-'}</p>
                  <p className="break-words"><strong>Phone:</strong> {quotation.enquiry?.contact_no || '-'}</p>
                  {(quotation.enquiry?.street || quotation.enquiry?.town || quotation.enquiry?.district) && (
                    <p className="break-words line-clamp-2"><strong>Addr:</strong> {[quotation.enquiry?.street, quotation.enquiry?.town, quotation.enquiry?.district].filter(Boolean).join(', ')}</p>
                  )}
                </div>
                <div className="space-y-0.5 min-w-0 overflow-hidden">
                  <p className="break-words"><strong>Enq No:</strong> {quotation.enquiry?.enquiry_no || '-'}</p>
                  <p className="break-words"><strong>Svc Date:</strong> {quotation.enquiry?.service_date ? new Date(quotation.enquiry.service_date).toLocaleDateString('en-GB') : '-'}</p>
                  <p className="break-words"><strong>Bore:</strong> {quotation.enquiry?.boreSize?.size || '-'}</p>
                  <p className="break-words"><strong>Rig:</strong> {quotation.enquiry?.rig?.rig_no || '-'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quotation Details */}
          <div className="mb-3">
            <h3 className="text-sm font-semibold mb-2 text-gray-900 print:text-black">Quotation Details</h3>
            <table className="w-full table-fixed border-collapse border border-gray-300">
              <colgroup>
                <col style={{ width: '7%' }} />
                <col style={{ width: '48%' }} />
                <col style={{ width: '9%' }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '18%' }} />
              </colgroup>
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-1 py-1 text-left text-xs font-semibold text-black">#</th>
                  <th className="border border-gray-300 px-2 py-1 text-left text-xs font-semibold text-black">Particulars</th>
                  <th className="border border-gray-300 px-1 py-1 text-center text-xs font-semibold text-black">Qty</th>
                  <th className="border border-gray-300 px-1 py-1 text-right text-xs font-semibold text-black">Rate</th>
                  <th className="border border-gray-300 px-1 py-1 text-right text-xs font-semibold text-black">Amount</th>
                </tr>
              </thead>
              <tbody>
                {quotation.quotationDetails?.sort((a, b) => {
                  // Custom sort for sl_no (handles 1.1, 1.2, 2, 3, 4 format)
                  const parseSlNo = (slNo: string | number) => {
                    const slNoStr = String(slNo);
                    const parts = slNoStr.split('.');
                    return parts.length === 2 ? [parseInt(parts[0]), parseInt(parts[1])] : [parseInt(parts[0]), 0];
                  };
                  const [aMain, aSub] = parseSlNo(a.sl_no);
                  const [bMain, bSub] = parseSlNo(b.sl_no);
                  return aMain !== bMain ? aMain - bMain : aSub - bSub;
                }).map((detail, index) => (
                  <tr key={detail.id}>
                    <td className="border border-gray-300 px-1 py-1 text-xs text-center text-black align-top">{index + 1}</td>
                    <td className="border border-gray-300 px-2 py-1 text-xs text-black align-top">
                      <div className="line-clamp-3 break-words" title={detail.particulars}>
                        {detail.particulars}
                      </div>
                    </td>
                    <td className="border border-gray-300 px-1 py-1 text-xs text-center text-black align-top">{detail.qty}</td>
                    <td className="border border-gray-300 px-1 py-1 text-xs text-right text-black align-top break-words">₹{detail.rate.toLocaleString('en-IN')}</td>
                    <td className="border border-gray-300 px-1 py-1 text-xs text-right text-black align-top break-words">₹{detail.amount.toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Total Summary */}
          <div className="mb-3">
            <div className="flex justify-end">
              <div className="w-full sm:w-1/2 max-w-md">
                <div className="bg-gray-50 p-2 rounded">
                  <div className="space-y-1 text-xs text-black">
                    <div className="flex justify-between gap-2">
                      <span className="font-medium whitespace-nowrap">Sub Total:</span>
                      <span className="text-right">₹{(quotation.sub_total || 0).toLocaleString('en-IN')}</span>
                    </div>
                    <hr className="border-gray-300" />
                    <div className="flex justify-between gap-2">
                      <span className="text-sm font-bold whitespace-nowrap">Total Amount:</span>
                      <span className="text-sm font-bold text-right">₹{(quotation.net_amount || 0).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="text-xs italic text-right">
                      GST 18% Extra
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Remarks */}
          {quotation.remarks && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-black mb-1">Remarks:</p>
              <p className="text-xs text-black whitespace-pre-line">{quotation.remarks}</p>
            </div>
          )}

          {/* Footer */}
          <div className="border-t border-gray-400 print:border-black pt-3">
            <div className="flex justify-between items-end gap-4">
              <div className="text-xs text-black space-y-0 min-w-0">
                <p className="break-words"><strong>Created by:</strong> {quotation.createdByUser?.name || '-'}</p>
                <p><strong>Created on:</strong> {quotation.created_at ? new Date(quotation.created_at).toLocaleDateString('en-GB') : '-'}</p>
              </div>
              <div className="text-center flex-shrink-0">
                <div className="border-t border-gray-600 print:border-black w-32 sm:w-40 mt-8 pt-1">
                  <p className="text-xs font-medium text-black">Authorized Signature</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationView;